import { useEffect, useMemo, useState } from 'react';
import ExcelJS from 'exceljs';
import { Dropdown, MenuProps } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import * as FileSaver from 'file-saver';
import { handleMenuClick, generateMenuItems } from 'utils/excelUtils';
import { getMapPaymentType } from 'constants/text';
import { formatCurrency, getEarliestValidDate, toLocalTime } from 'utils';
import useCommonStore from 'stores/useCommon';
import { useGetAllPayments, useGetTransactionsPayment } from 'hooks/useGetPayments';

import { PermissionActionTypes } from 'helper/type/action.type';
import IBButton from 'helper/permission.action';
import { usePaymentMethodOptions } from 'hooks/usePaymentMethodOptions';
import type { AllPaymentItem } from 'services/api/type/payment.type';

interface Props {
  fileName: string;
  selectedPaymentState?: string;
  selectedPaymentMethod?: string;
  englishType?: boolean;
  filterOptions: any;
  t: (key: string) => string;
}

const ExportTransactions = ({
  fileName,
  selectedPaymentState,
  selectedPaymentMethod,
  englishType,
  filterOptions,
  t
}: Props) => {
  const MAP_PAYMENT_TYPE = getMapPaymentType(t);
  const { paymentMethodNames } = usePaymentMethodOptions(t);
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [enableV1, setEnableV1] = useState<boolean>(false);
  const [enableV2, setEnableV2] = useState<boolean>(false);

  const { data: dataToExportV1 } = useGetAllPayments(filterOptions, 0, 0, enableV1);
  const { data: dataToExportV2 } = useGetTransactionsPayment(filterOptions, 0, 0, enableV2);

  const { currentBranch } = useCommonStore();

  useEffect(() => {
    if (isLoadingExport) {
      setEnableV1(false);
      setEnableV2(false);
    } else {
      setEnableV1(!englishType);
      setEnableV2(!!englishType);
    }
  }, [isLoadingExport, englishType]);

  const dataToExport = useMemo(() => {
    return (englishType ? dataToExportV2 || [] : dataToExportV1 || []).filter(
      item => item.state !== 'cancelled'
    );
  }, [englishType, dataToExportV1, dataToExportV2]);

  const fromDate = getEarliestValidDate(filterOptions.start_date);
  const toDate = getEarliestValidDate(filterOptions.end_date);
  const totals = useMemo(() => {
    const totalAmount = dataToExport?.reduce((sum, item) => sum + (item.amount || 0), 0);
    const totalCustomers = dataToExport?.reduce(
      (sum, item) => sum + (item.customer_name ? 1 : 0),
      0
    );
    return { totalAmount, totalCustomers };
  }, [dataToExport]);

  const exportToCSV = async () => {
    const fileType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    try {
      setIsLoadingExport(true);

      const workbook = new ExcelJS.Workbook();
      const sheetName = t('managePage.transactionPage.export.sheetName');
      const sheet = workbook.addWorksheet(sheetName, {
        views: [
          {
            showGridLines: false
          }
        ]
      });

      // Title
      sheet.mergeCells(englishType ? 'A1:L1' : 'A1:K1');
      sheet.getCell('A1').value = currentBranch?.name?.toUpperCase() || '';
      sheet.getCell('A1').font = { bold: true, size: 14 };
      sheet.getCell('A1').alignment = { horizontal: 'center' };

      sheet.mergeCells(englishType ? 'A2:L2' : 'A2:K2');
      sheet.getCell('A2').value = t('managePage.transactionPage.export.reportTitle');
      sheet.getCell('A2').font = { size: 11, bold: true };
      sheet.getCell('A2').alignment = { horizontal: 'center' };

      sheet.getCell('A4').value = t('common.time.fromDate');
      sheet.getCell('A4').font = { bold: true };
      sheet.getCell('B4').value = fromDate;
      sheet.getCell('C4').value = t('common.time.toDate');
      sheet.getCell('C4').font = { bold: true };
      sheet.getCell('D4').value = toDate;
      sheet.getCell('E4').value = t('common.bookingData.status');
      sheet.getCell('E4').font = { bold: true };
      sheet.getCell('F4').value = selectedPaymentState || t('common.statuses.all');
      sheet.getCell('G4').value = t('managePage.transactionPage.common.paymentMethod');
      sheet.getCell('G4').font = { bold: true };
      sheet.getCell('H4').value = selectedPaymentMethod || t('common.statuses.all');

      // Header row (V2 / englishType includes debit partner to match transaction table)
      sheet.getRow(5).values = [
        t('managePage.transactionPage.export.id'),
        t('managePage.transactionPage.export.bookingCode'),
        t('common.bookingData.originalBookingId'),
        t('managePage.transactionPage.common.paymentDate'),
        t('common.note'),
        t('managePage.transactionPage.common.amount'),
        t('managePage.transactionPage.common.paymentType'),
        t('managePage.transactionPage.common.paymentMethod'),
        t('managePage.transactionPage.common.userName'),
        t('common.customer.name'),
        t('common.bookingData.debitPartner'),
        t('managePage.transactionPage.common.desc')
      ];
      sheet.getRow(5).eachCell(cell => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        };
        cell.font = { bold: true };
        cell.alignment = { horizontal: 'left' };
      });

      sheet.columns = englishType
        ? [
            { key: 'id', width: 15 },
            { key: 'booking_id', width: 16 },
            { key: 'original_booking_id', width: 16 },
            { key: 'payment_date', width: 20 },
            { key: 'note', width: 30 },
            { key: 'amount', width: 20 },
            { key: 'payment_type', width: 15 },
            { key: 'payment_method', width: 25 },
            { key: 'user_name', width: 20 },
            { key: 'customer_name', width: 25 },
            { key: 'debit_partner_name', width: 25 },
            { key: 'description', width: 90 }
          ]
        : [
            { key: 'id', width: 15 },
            { key: 'booking_id', width: 16 },
            { key: 'original_booking_id', width: 16 },
            { key: 'payment_date', width: 20 },
            { key: 'note', width: 30 },
            { key: 'amount', width: 20 },
            { key: 'payment_type', width: 15 },
            { key: 'payment_method', width: 25 },
            { key: 'user_name', width: 20 },
            { key: 'debit_partner_name', width: 25 },
            { key: 'customer_name', width: 25 },
            { key: 'description', width: 90 }
          ];

      dataToExport.forEach(item => {
        const row = sheet.addRow(
          englishType
            ? {
                id: item.id,
                booking_id: item.booking_id ? item.booking_id : '',
                original_booking_id: item.original_booking_id ? item.original_booking_id : '',
                payment_date: toLocalTime(item.payment_date, 'DD/MM/YYYY HH:mm'),
                note: item.note ? item.note : '',
                amount: formatCurrency(item.amount),
                payment_type: item.payment_type,
                payment_method: item.payment_method,
                user_name: item.user_name,
                customer_name: item.customer_name,
                debit_partner_name: (item as AllPaymentItem).debit_partner_name || '',
                description: item.description
              }
            : {
                id: item.id,
                booking_id: item.booking_id ? item.booking_id : '',
                original_booking_id: item.original_booking_id ? item.original_booking_id : '',
                payment_date: toLocalTime(item.payment_date, 'DD/MM/YYYY HH:mm'),
                note: item.note ? item.note : '',
                amount: formatCurrency(item.amount),
                payment_type: MAP_PAYMENT_TYPE[item.payment_type],
                payment_method:
                  paymentMethodNames[item.payment_method as keyof typeof paymentMethodNames] ?? '',
                user_name: item.user_name,
                customer_name: item.customer_name,
                debit_partner_name: (item as AllPaymentItem).debit_partner_name || '',
                description: item.description
              }
        );

        row.eachCell({ includeEmpty: true }, cell => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          cell.alignment = { horizontal: 'left' };
        });
      });

      const totalRowIndex = dataToExport.length + 6;
      // Columns: A id, B booking, C original booking, D payment date, E note, F amount, …, J customer, K debit, L desc
      const amountCol = 'F';
      const customerCol = 'J';

      sheet.mergeCells(`A${totalRowIndex}:E${totalRowIndex}`);
      sheet.getCell(`A${totalRowIndex}`).value = t('common.total');
      sheet.getCell(`A${totalRowIndex}`).font = { bold: true };
      sheet.getCell(`A${totalRowIndex}`).alignment = { horizontal: 'right' };

      sheet.getCell(`${amountCol}${totalRowIndex}`).value = formatCurrency(totals.totalAmount);
      sheet.getCell(`${amountCol}${totalRowIndex}`).font = { bold: true };
      sheet.getCell(`${amountCol}${totalRowIndex}`).alignment = { horizontal: 'right' };

      sheet.getCell(`${customerCol}${totalRowIndex}`).value = totals.totalCustomers;
      sheet.getCell(`${customerCol}${totalRowIndex}`).font = { bold: true };
      sheet.getCell(`${customerCol}${totalRowIndex}`).alignment = { horizontal: 'right' };

      const totalBorderCols = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
      totalBorderCols.forEach(col => {
        sheet.getCell(`${col}${totalRowIndex}`).border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } }
        };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const data = new Blob([buffer], { type: fileType });
      FileSaver.saveAs(data, fileName + fileExtension);
    } catch (e) {
      return { status: 'ERROR', error: e };
    } finally {
      setIsLoadingExport(false);
    }
  };

  const handleClickMenu = (menu: any) => {
    handleMenuClick(menu.key, exportToCSV);
  };
  const items: MenuProps['items'] = useMemo(() => generateMenuItems(), []);

  return (
    <Dropdown
      menu={{ items, onClick: handleClickMenu }}
      trigger={['click']}
      overlayClassName="menu-user"
    >
      <IBButton
        permission={PermissionActionTypes.TRANSACTION_LIST_EXPORT}
        icon={<DownloadOutlined />}
        loading={isLoadingExport}
      >
        {t('common.actions.exportData')}
      </IBButton>
    </Dropdown>
  );
};

export default ExportTransactions;
